<template>
  <div class="w-full">
    <!-- <div v-if="label" class="flex items-center gap-[4px]"> -->
    <label
      v-if="label"
      :for="label"
      class="block mb-2 text-nowrap overflow-hidden text-ellipsis title"
      :title="label"
    >
      {{ label }}
    </label>

    <div class="relative">
      <div class="">
        <div
          v-show="icon"
          class="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none"
        >
          <UserIcon
            v-if="icon == 'userIcon'"
            class="text-secondary-light w-[24px] h-[24px] align-middle"
          />
          <PasswordIcon
            v-if="icon == 'passwordIcon'"
            class="text-secondary-light w-[24px] h-[24px] align-middle"
          />
        </div>

        <!-- input จริง -->
        <input
          ref="input"
          :type="inputType"
          :id="id || label"
          :value="modelValue"
          :maxlength="computedMaxlength"
          :readonly="readonly"
          :placeholder="placeholder"
          :required="required"
          :disabled="disabled"
          :step="step"
          :inputmode="digitsOnly ? 'numeric' : null"
          :pattern="digitsOnly ? '\\d*' : null"
          class="w-full h-[42px] leading-[20px] text-[14px] text-ellipsis px-2.5 py-3 !text-primary-dark border border-grey-light placeholder-secondary-light font-normal bg-white rounded-[6px] focus:outline-none disabled:border-secondary-light focus:border-[#1F1D1F] disabled:bg-[#F3F2F2] disabled:text-secondary"
          :class="[
            icon ? 'ps-12' : '',
            inputType === 'password' || iconName ? 'pe-12' : '',
            boxShadow === true
              ? 'shadow-[0px_3px_4px_0px_#00000033_inset]'
              : '',
            error ? '!border-[#EB5554]' : '',
            disabled && modelValue ? '!text-[#1F1D1F]' : '',
            inputClass,
            warningMessage ? '!border-[#EB5554]' : '',
          ]"
          autocomplete="on"
          @keydown="
            (e) => {
              checkInputEnglish(e);
              emit('keydown', e);
            }
          "
          @beforeinput="onBeforeInput"
          @paste="onPaste"
          @input="onInput($event.target.value)"
          @focus="emit('focus', $event)"
          @blur="emit('blur', $event)"
        />

        <div
          v-if="inputType == 'password'"
          class="absolute end-0 inset-y-0 flex items-center pe-3.5 cursor-pointer"
          @click="togglePassword"
        >
          <EyeIcon class="text-[#555055]" :isOpen="showPassword" />
        </div>

        <div
          v-if="iconName"
          class="absolute end-0 inset-y-0 flex items-center pe-3.5 cursor-pointer"
          @click="$emit('onClickIcon')"
        >
          <component
            :is="iconName"
            class="text-primary w-[20px] h-[20px] align-middle"
          />
        </div>
      </div>

      <!-- don't remove this because need to reserve space to show error message -->
      <small v-if="error || Array.isArray(error)" class="textError block mt-[8px] sm:mt-0 sm:absolute top-[50px] label-M">{{
        Array.isArray(error) ? error[0] : error
      }}</small>
      <!-- <small class="sm:hidden mt-[8px] textError label-M">
        {{     Array.isArray(error) ? error[0] : error }}
      </small> -->
    </div>
    <!-- <div
      v-if="warningMessage || error"
      :class="['textError', { 'mt-[8px]': warningMessage }]"
    >
      {{ warningMessage || (error && Array.isArray(error) ? error[0] : error)}}
    </div> -->
  </div>
</template>

<script setup>
import { ref, useTemplateRef, computed } from "vue";
import EyeIcon from "../../assets/icons/eye.icon.vue";
import UserIcon from "@/assets/icons/user.icon.vue";
import PasswordIcon from "@/assets/icons/password.icon.vue";

const emit = defineEmits([
  "update:modelValue",
  "onClickIcon",
  "focus",
  "blur",
  "keydown",
]);

const props = defineProps({
  id: String,
  label: String,
  modelValue: [String, Number],
  placeholder: String,
  inputType: {
    default: "text",
    type: String,
  },
  error: null,
  disabled: Boolean,
  required: Boolean,
  icon: String,
  boxShadow: Boolean,
  iconName: String,
  inputClass: "",
  warningMessage: {
    type: [String, Boolean],
    default: "",
  },
  checkOnlyEnglish: {
    type: Boolean,
    default: false,
  },
  step: {
    type: [String, Number],
    default: "any",
  },
  readonly: {
    type: Boolean,
    default: false,
  },

  // เปิดโหมด "รับเฉพาะตัวเลข" (ทำงานกับ inputType="text" ด้วย)
  digitsOnly: {
    type: Boolean,
    default: false,
  },
  // จำกัดความยาวเวลาที่ digitsOnly=true (ถ้าไม่ส่ง ใช้ค่าเดิมของ tel=10, อื่นๆ ไม่จำกัด)
  digitsMaxLength: {
    type: [Number, String, null],
    default: null,
  },
});

const CONTROL_KEYS = [
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
  "Home",
  "End",
];

const input = useTemplateRef("input");
const showPassword = ref(false);

const computedMaxlength = computed(() => {
  // เดิม: ถ้าเป็น tel จำกัด 10
  const telDefault = props.inputType === "tel" ? 10 : null;
  // ใหม่: ถ้าเปิด digitsOnly แล้วส่ง digitsMaxLength มาก็ใช้ค่านั้น
  if (
    props.digitsOnly &&
    props.digitsMaxLength != null &&
    props.digitsMaxLength !== ""
  ) {
    return String(props.digitsMaxLength);
  }
  if (props.digitsMaxLength != null && props.digitsMaxLength !== "") {
    return String(props.digitsMaxLength);
  }
  return telDefault != null ? String(telDefault) : null;
});

const sanitizeDigits = (v) => String(v ?? "").replace(/\D/g, "");

/** อัปเดตค่า (sanitize ถ้าจำเป็น) */
const onInput = (value) => {
  let out = value;
  if (props.digitsOnly) {
    out = sanitizeDigits(out);
    const max = Number(computedMaxlength.value ?? Infinity);
    if (Number.isFinite(max)) out = out.slice(0, max);
  }
  emit("update:modelValue", out);
};

const togglePassword = () => {
  showPassword.value = !showPassword.value;
  input.value.type = showPassword.value ? "text" : "password";
};

const checkInputEnglish = (e) => {
  //  ถ้า inputType="number"
  if (props.inputType === "number") {
    if (CONTROL_KEYS.includes(e.key)) return;
    if (/^\d$/.test(e.key)) return;
    if (e.key === ".") {
      if (e.target.value.includes(".")) e.preventDefault();
      return;
    }
    e.preventDefault();
    return;
  }

  // ใหม่: ถ้าเปิด digitsOnly บน text -> อนุญาตเฉพาะตัวเลข + control keys
  if (props.digitsOnly) {
    if (CONTROL_KEYS.includes(e.key)) return;
    if (/^\d$/.test(e.key)) return;
    // อื่น ๆ บล็อก ถ้าไม่ใช่ ปิดไว้
    // e.preventDefault();
    return;
  }

  // only English
  if (props.checkOnlyEnglish) {
    if (CONTROL_KEYS.includes(e.key)) return;
    const char = e.key?.length === 1 ? e.key : String.fromCharCode(e.keyCode);
    if (/^[a-zA-Z0-9._@-]*$/.test(char)) return true;
    else e.preventDefault();
  }
};

/** กันตั้งแต่ beforeinput (ทั้ง Chrome/Firefox) เมื่อ digitsOnly เปิด */
const onBeforeInput = (e) => {
  if (!props.digitsOnly) return;

  const editOps = new Set([
    "deleteContentBackward",
    "deleteContentForward",
    "deleteByCut",
    "insertFromDrop",
    "insertLineBreak",
    "insertParagraph",
  ]);
  if (editOps.has(e.inputType)) return;

  if (e.inputType === "insertText") {
    const data = e.data ?? "";
    if (/\D/.test(data)) {
      e.preventDefault();
      return;
    }
    // เช็คความยาวล่วงหน้าถ้ากำหนด maxlength
    const max = Number(computedMaxlength.value ?? Infinity);
    if (Number.isFinite(max)) {
      const el = e.target;
      const cur = String(el.value ?? "");
      const selStart = el.selectionStart ?? cur.length;
      const selEnd = el.selectionEnd ?? cur.length;
      const nextLen = cur.length - (selEnd - selStart) + data.length;
      if (nextLen > max) {
        e.preventDefault();
        return;
      }
    }
  }
};

/** จัดการ paste เมื่อ digitsOnly เปิด */
const onPaste = (e) => {
  if (!props.digitsOnly) return;

  const pasted = e.clipboardData?.getData("text") ?? "";
  const digits = sanitizeDigits(pasted);
  e.preventDefault();

  // แทรกลงในตำแหน่งเคอร์เซอร์ แล้วคุม maxlength
  const el = e.target;
  const cur = String(el.value ?? "");
  const selStart = el.selectionStart ?? cur.length;
  const selEnd = el.selectionEnd ?? cur.length;
  const max = Number(computedMaxlength.value ?? Infinity);

  let next = cur.slice(0, selStart) + digits + cur.slice(selEnd);
  if (Number.isFinite(max)) next = next.slice(0, max);

  emit("update:modelValue", next);

  // เคลื่อน caret ไปท้ายส่วนที่วาง
  requestAnimationFrame(() => {
    const pos = Math.min(selStart + digits.length, next.length);
    try {
      el.setSelectionRange(pos, pos);
    } catch {}
  });
};
</script>

<style lang="scss" scoped>
.no-spinner::-webkit-outer-spin-button,
.no-spinner::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[readonly] {
  background-color: #f3f2f2;
  color: #dfdcdc !important;

  &::placeholder {
    color: #dfdcdc !important;
  }
}

.title {
  color: #1f1d1f;

  /* fields/title */
  font-family: Kanit;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  letter-spacing: 0.1px;
}
@media screen and (max-width: 64rem) {
  .PC_Body1-R {
    font-size: 16px !important;
  }
}
</style>
